import { defineSemanticTokens } from "@chakra-ui/react-v3";

export const semanticColors = defineSemanticTokens.colors({
  bg: {
    default: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.black}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.black}",
      },
    },
    container: {
      value: {
        _light: "{colors.white}",
        _dark: "{colors.gray.950}",
      },
    },
    inverted: {
      value: {
        _light: "{colors.black}",
        _dark: "{colors.gray.50}",
      },
    },
    code: {
      value: {
        _light: "{colors.black}",
        _dark: "{colors.black}",
      },
    },
  },

  // Grain colors for the decorative DitherGradient. Light values match the
  // neutral/indigo subtle tones; dark values are lifted off bg.container so
  // the dither stays visible against the near-black dark background.
  dither: {
    neutral: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.800}",
      },
    },
    highlight: {
      value: {
        _light: "{colors.indigo.200}",
        _dark: "{colors.indigo.700}",
      },
    },
  },

  border: {
    container: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.800}",
      },
    },
    divider: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.800}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.gray.200}",
        _dark: "{colors.gray.800}",
      },
    },
    inverted: {
      value: {
        _light: "{colors.black}",
        _dark: "{colors.white}",
      },
    },
    contrast: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.gray.50}",
      },
    },
  },

  fg: {
    default: {
      value: {
        _light: "{colors.black}",
        _dark: "{colors.gray.50}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.gray.700}",
        _dark: "{colors.gray.400}",
      },
    },
    muted: {
      value: {
        _light: "{colors.gray.600}",
        _dark: "{colors.gray.500}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.gray.400}",
        _dark: "{colors.gray.600}",
      },
    },
    inverted: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.black}",
      },
    },
    contrast: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.gray.50}",
      },
    },
    code: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.gray.50}",
      },
    },
  },

  neutral: {
    default: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.black}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.900}",
      },
    },
    muted: {
      value: {
        _light: "{colors.gray.200}",
        _dark: "{colors.gray.800}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.gray.300}",
        _dark: "{colors.gray.700}",
      },
    },
    focusRing: {
      value: {
        _light: "{colors.gray.200}",
        _dark: "{colors.gray.800}",
      },
    },
    fg: {
      value: {
        _light: "{colors.gray.700}",
        _dark: "{colors.gray.400}",
      },
    },
    solid: {
      value: {
        _light: "{colors.gray.500}",
        _dark: "{colors.gray.700}",
      },
    },
  },

  highlight: {
    default: {
      value: {
        _light: "{colors.indigo.50}",
        _dark: "{colors.indigo.950}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.indigo.100}",
        _dark: "{colors.indigo.900}",
      },
    },
    muted: {
      value: {
        _light: "{colors.indigo.200}",
        _dark: "{colors.indigo.800}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.indigo.300}",
        _dark: "{colors.indigo.700}",
      },
    },
    focusRing: {
      value: {
        _light: "{colors.indigo.200}",
        _dark: "{colors.indigo.800}",
      },
    },
    fg: {
      value: {
        _light: "{colors.indigo.600}",
        _dark: "{colors.indigo.400}",
      },
    },
    solid: {
      value: {
        _light: "{colors.indigo.500}",
        _dark: "{colors.indigo.600}",
      },
    },
  },

  success: {
    default: {
      value: {
        _light: "{colors.green.50}",
        _dark: "{colors.green.950}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.green.100}",
        _dark: "{colors.green.900}",
      },
    },
    muted: {
      value: {
        _light: "{colors.green.200}",
        _dark: "{colors.green.800}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.green.300}",
        _dark: "{colors.green.700}",
      },
    },
    focusRing: {
      value: {
        _light: "{colors.green.200}",
        _dark: "{colors.green.800}",
      },
    },
    fg: {
      value: {
        _light: "{colors.green.600}",
        _dark: "{colors.green.400}",
      },
    },
    solid: {
      value: {
        _light: "{colors.green.500}",
        _dark: "{colors.green.600}",
      },
    },
  },

  warning: {
    default: {
      value: {
        _light: "{colors.sand.50}",
        _dark: "{colors.sand.950}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.sand.100}",
        _dark: "{colors.sand.900}",
      },
    },
    muted: {
      value: {
        _light: "{colors.sand.200}",
        _dark: "{colors.sand.800}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.sand.300}",
        _dark: "{colors.sand.700}",
      },
    },
    focusRing: {
      value: {
        _light: "{colors.sand.200}",
        _dark: "{colors.sand.800}",
      },
    },
    fg: {
      value: {
        _light: "{colors.sand.600}",
        _dark: "{colors.sand.500}",
      },
    },
    fgEmphasized: {
      value: {
        _light: "{colors.sand.700}",
        _dark: "{colors.sand.400}",
      },
    },
    solid: {
      value: {
        _light: "{colors.sand.500}",
        _dark: "{colors.sand.600}",
      },
    },
  },

  error: {
    default: {
      value: {
        _light: "{colors.red.100}",
        _dark: "{colors.red.950}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.red.200}",
        _dark: "{colors.red.900}",
      },
    },
    muted: {
      value: {
        _light: "{colors.red.300}",
        _dark: "{colors.red.800}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.red.400}",
        _dark: "{colors.red.700}",
      },
    },
    focusRing: {
      value: {
        _light: "{colors.red.200}",
        _dark: "{colors.red.800}",
      },
    },
    fg: {
      value: {
        _light: "{colors.red.600}",
        _dark: "{colors.red.500}",
      },
    },
    solid: {
      value: {
        _light: "{colors.red.500}",
        _dark: "{colors.red.600}",
      },
    },
  },

  fatal: {
    emphasized: {
      value: {
        _light: "{colors.pink.300}",
        _dark: "{colors.pink.400}",
      },
    },
    solid: {
      value: {
        _light: "{colors.pink.600}",
        _dark: "{colors.pink.700}",
      },
    },
  },

  gradient: {
    blackToHighlight: {
      90100: {
        value:
          "linear-gradient(90deg, {colors.black}, {colors.highlight.solid}) 0 0/100% 100%",
      },
      141100: {
        value: "linear-gradient(141deg, #000000,#745AFF) 0 0/100% 100%",
      },
    },
    highlight: {
      90400: {
        value:
          "linear-gradient(90deg, {colors.highlight.solid}, {colors.highlight.emphasized}) 0 0/400% 100%",
      },
    },
    highlightToPink: {
      90: {
        value: {
          _light:
            "linear-gradient(90deg, {colors.indigo.100} 0%, {colors.pink.100} 100%)",
          _dark:
            "linear-gradient(90deg, {colors.indigo.900} 0%, {colors.pink.900} 100%)",
        },
      },
    },
    softGradient: {
      90200: {
        value: {
          _light:
            "linear-gradient(90deg, {colors.indigo.100} 0%, {colors.bg.container} 50%, {colors.pink.100} 100%) 0 0/200% 100%",
          _dark:
            "linear-gradient(90deg, {colors.indigo.900} 0%, {colors.bg.container} 50%, {colors.pink.900} 100%) 0 0/200% 100%",
        },
      },
    },
    timescale: {
      value: {
        _light:
          "linear-gradient(90deg, {colors.highlight.fg} 0%, {colors.pink.600} 100%)",
        _dark:
          "linear-gradient(90deg, {colors.highlight.fg} 0%, {colors.pink.500} 100%)",
      },
    },
    popsql: {
      value: {
        _light:
          "linear-gradient(90deg, {colors.sand.600} 0%, {colors.error.fg} 100%)",
        _dark:
          "linear-gradient(90deg, {colors.sand.500} 0%, {colors.error.fg} 100%)",
      },
    },
    popSql: {
      default: {
        value: "linear-gradient(180deg, #FD8C3D 0%, #EA4476 100%)",
      },
      hover: {
        value: "linear-gradient(180deg, #FD8C3D 0%, #EA4476 70%, #EA4476 100%)",
      },
      active: {
        value: "linear-gradient(180deg, #FD8C3D 0%, #EA4476 30%, #EA4476 100%)",
      },
    },
  },

  avatar: {
    lagoon: {
      value: {
        _light: "{colors.green.300}",
        _dark: "{colors.green.700}",
      },
    },
    glacier: {
      value: {
        _light: "{colors.indigo.300}",
        _dark: "{colors.indigo.700}",
      },
    },
    sky: {
      value: {
        _light: "{colors.sky.400}",
        _dark: "{colors.sky.600}",
      },
    },
    aqua: {
      value: {
        _light: "{colors.aqua.400}",
        _dark: "{colors.aqua.600}",
      },
    },
    coral: {
      value: {
        _light: "{colors.red.400}",
        _dark: "{colors.red.600}",
      },
    },
    meadow: {
      value: "{colors.green.500}",
    },
    amber: {
      value: "{colors.sand.500}",
    },
    fuchsia: {
      value: "{colors.pink.500}",
    },
    orchid: {
      value: "{colors.purple.500}",
    },
    denim: {
      value: {
        _light: "{colors.sky.600}",
        _dark: "{colors.sky.400}",
      },
    },
    lake: {
      value: {
        _light: "{colors.aqua.600}",
        _dark: "{colors.aqua.400}",
      },
    },
    crimson: {
      value: {
        _light: "{colors.red.600}",
        _dark: "{colors.red.400}",
      },
    },
    olive: {
      value: {
        _light: "{colors.electricYellow.700}",
        _dark: "{colors.electricYellow.300}",
      },
    },
    rusty: {
      value: {
        _light: "{colors.sand.700}",
        _dark: "{colors.sand.300}",
      },
    },
    ultramarine: {
      value: {
        _light: "{colors.indigo.800}",
        _dark: "{colors.indigo.200}",
      },
    },
    charcoal: {
      value: {
        _light: "{colors.gray.950}",
        _dark: "{colors.gray.100}",
      },
    },
  },

  codeSnippet: {
    sky: {
      value: {
        _light: "{colors.sky.600}",
        _dark: "{colors.sky.400}",
      },
    },
    aqua: {
      value: {
        _light: "{colors.aqua.600}",
        _dark: "{colors.aqua.400}",
      },
    },
    green: {
      value: {
        _light: "{colors.green.600}",
        _dark: "{colors.green.400}",
      },
    },
    electricYellow: {
      value: {
        _light: "{colors.electricYellow.600}",
        _dark: "{colors.electricYellow.400}",
      },
    },
    pink: {
      value: {
        _light: "{colors.pink.600}",
        _dark: "{colors.pink.400}",
      },
    },
    purple: {
      value: {
        _light: "{colors.purple.600}",
        _dark: "{colors.purple.400}",
      },
    },
    indigo: {
      value: {
        _light: "{colors.indigo.600}",
        _dark: "{colors.indigo.400}",
      },
    },
    sand: {
      value: {
        _light: "{colors.sand.600}",
        _dark: "{colors.sand.400}",
      },
    },
  },

  chart: {
    sky: {
      strong: {
        value: {
          _light: "{colors.sky.500}",
          _dark: "{colors.sky.500}",
        },
      },
      medium: {
        value: {
          _light: "{colors.sky.300}",
          _dark: "{colors.sky.700}",
        },
      },
      soft: {
        value: {
          _light: "{colors.sky.100}",
          _dark: "{colors.sky.900}",
        },
      },
      gradient: {
        value:
          "linear-gradient(to bottom, {colors.chart.sky.soft}, {colors.bg.default})",
      },
    },
    sand: {
      strong: {
        value: {
          _light: "{colors.sand.500}",
          _dark: "{colors.sand.500}",
        },
      },
      medium: {
        value: {
          _light: "{colors.sand.300}",
          _dark: "{colors.sand.700}",
        },
      },
      soft: {
        value: {
          _light: "{colors.sand.100}",
          _dark: "{colors.sand.900}",
        },
      },
      gradient: {
        value:
          "linear-gradient(to bottom, {colors.chart.sand.soft}, {colors.bg.default})",
      },
    },
    purple: {
      strong: {
        value: {
          _light: "{colors.purple.500}",
          _dark: "{colors.purple.500}",
        },
      },
      medium: {
        value: {
          _light: "{colors.purple.300}",
          _dark: "{colors.purple.700}",
        },
      },
      soft: {
        value: {
          _light: "{colors.purple.100}",
          _dark: "{colors.purple.900}",
        },
      },
      gradient: {
        value:
          "linear-gradient(to bottom, {colors.chart.purple.soft}, {colors.bg.default})",
      },
    },
    aqua: {
      strong: {
        value: {
          _light: "{colors.aqua.500}",
          _dark: "{colors.aqua.500}",
        },
      },
      medium: {
        value: {
          _light: "{colors.aqua.300}",
          _dark: "{colors.aqua.700}",
        },
      },
      soft: {
        value: {
          _light: "{colors.aqua.100}",
          _dark: "{colors.aqua.900}",
        },
      },
      gradient: {
        value:
          "linear-gradient(to bottom, {colors.chart.aqua.soft}, {colors.bg.default})",
      },
    },
    red: {
      strong: {
        value: {
          _light: "{colors.red.500}",
          _dark: "{colors.red.500}",
        },
      },
      medium: {
        value: {
          _light: "{colors.red.300}",
          _dark: "{colors.red.700}",
        },
      },
      soft: {
        value: {
          _light: "{colors.red.100}",
          _dark: "{colors.red.900}",
        },
      },
      gradient: {
        value:
          "linear-gradient(to bottom, {colors.chart.red.soft}, {colors.bg.default})",
      },
    },
    pink: {
      strong: {
        value: {
          _light: "{colors.pink.500}",
          _dark: "{colors.pink.500}",
        },
      },
      medium: {
        value: {
          _light: "{colors.pink.300}",
          _dark: "{colors.pink.700}",
        },
      },
      soft: {
        value: {
          _light: "{colors.pink.100}",
          _dark: "{colors.pink.900}",
        },
      },
      gradient: {
        value:
          "linear-gradient(to bottom, {colors.chart.pink.soft}, {colors.bg.default})",
      },
    },
  },

  storage: {
    rowstore: {
      value: {
        _light: "{colors.sky.200}",
        _dark: "{colors.sky.400}",
      },
    },
    columnstore: {
      value: {
        _light: "{colors.indigo.600}",
        _dark: "{colors.indigo.600}",
      },
    },
    tiered: {
      value: {
        _light: "{colors.aqua.400}",
        _dark: "{colors.aqua.500}",
      },
    },
  },

  tab: {
    bg: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.black}",
      },
    },
  },

  diagramTile: {
    bg: {
      inactive: {
        value: {
          _light: "{colors.gray.50}",
          _dark: "{colors.whiteAlpha.100}",
        },
      },
      active: {
        value: {
          _light: "{colors.white}",
          _dark: "{colors.gray.950}",
        },
      },
    },
  },

  // The code fields on the service overview's Service Info card. Light keeps
  // the inverted look; dark lifts them off the near-black card to gray.
  serviceInfoInput: {
    bg: {
      value: {
        _light: "{colors.black}",
        _dark: "{colors.gray.700}",
      },
    },
  },

  tooltip: {
    bg: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.900}",
      },
    },
  },

  illustration: {
    fg: {
      value: {
        _light: "{colors.black}",
        _dark: "{colors.gray.50}",
      },
    },
    fgMuted: {
      value: {
        _light: "{colors.gray.700}",
        _dark: "{colors.gray.400}",
      },
    },
    fgSubtle: {
      value: "{colors.gray.500}",
    },
    fgInverted: {
      value: {
        _light: "{colors.white}",
        _dark: "{colors.gray.950}",
      },
    },
    border: {
      value: {
        _light: "{colors.gray.200}",
        _dark: "{colors.gray.800}",
      },
    },
    surface: {
      value: {
        _light: "{colors.gray.50}",
        _dark: "{colors.gray.950}",
      },
    },
    gridLine: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.900}",
      },
    },
    gridDashed: {
      value: {
        _light: "{colors.gray.300}",
        _dark: "{colors.gray.700}",
      },
    },
    accent: {
      value: {
        _light: "{colors.indigo.500}",
        _dark: "{colors.indigo.500}",
      },
    },
    accentSoft: {
      value: {
        _light: "{colors.indigo.200}",
        _dark: "{colors.indigo.800}",
      },
    },
    accentTint: {
      value: {
        _light: "{colors.indigo.100}",
        _dark: "{colors.indigo.900}",
      },
    },
    accentToFg: {
      value: {
        _light: "{colors.indigo.500}",
        _dark: "{colors.gray.50}",
      },
    },
    sky: {
      value: {
        _light: "{colors.sky.300}",
        _dark: "{colors.sky.600}",
      },
    },
    error: {
      value: "{colors.red.500}",
    },
    errorSoft: {
      value: {
        _light: "{colors.red.400}",
        _dark: "{colors.red.600}",
      },
    },
  },

  button: {
    default: {
      value: {
        _light: "{colors.gray.100}",
        _dark: "{colors.gray.900}",
      },
    },
    subtle: {
      value: {
        _light: "{colors.gray.200}",
        _dark: "{colors.gray.800}",
      },
    },
    muted: {
      value: {
        _light: "{colors.gray.400}",
        _dark: "{colors.gray.600}",
      },
    },
    emphasized: {
      value: {
        _light: "{colors.gray.700}",
        _dark: "{colors.gray.200}",
      },
    },
    solid: {
      value: {
        _light: "{colors.gray.900}",
        _dark: "{colors.indigo.100}",
      },
    },
    inverted: {
      default: {
        bg: {
          value: "{colors.white}",
        },
        fg: {
          value: "{colors.black}",
        },
        border: {
          value: "{colors.black}",
        },
      },
      hover: {
        bg: {
          value: "{colors.gray.800}",
        },
        fg: {
          value: "{colors.white}",
        },
        border: {
          value: "{colors.gray.800}",
        },
      },
      focus: {
        bg: {
          value: "{colors.gray.700}",
        },
        fg: {
          value: "{colors.white}",
        },
        border: {
          value: "{colors.black}",
        },
      },
      disabled: {
        bg: {
          value: "{colors.gray.100}",
        },
        fg: {
          value: "{colors.gray.400}",
        },
        border: {
          value: "{colors.gray.100}",
        },
      },
    },
  },

  switch: {
    thumb: {
      value: "{colors.white}",
    },
  },

  tag: {
    sky: {
      fg: {
        value: {
          _light: "{colors.sky.600}",
          _dark: "{colors.sky.400}",
        },
      },
      bg: {
        value: {
          _light: "{colors.sky.100}",
          _dark: "{colors.sky.900}",
        },
      },
    },
    green: {
      fg: {
        value: {
          _light: "{colors.green.600}",
          _dark: "{colors.green.400}",
        },
      },
      bg: {
        value: {
          _light: "{colors.green.100}",
          _dark: "{colors.green.900}",
        },
      },
    },
    pink: {
      fg: {
        value: {
          _light: "{colors.pink.600}",
          _dark: "{colors.pink.400}",
        },
      },
      bg: {
        value: {
          _light: "{colors.pink.100}",
          _dark: "{colors.pink.900}",
        },
      },
    },
  },

  dialog: {
    backdrop: {
      value: "{colors.blackAlpha.600}",
    },
  },
});
